import { useCallback, useEffect, useState } from 'react';
import { SubscriptionStatus, subscriptionAPI } from '../services/api';

interface UseSubscriptionStatusOptions {
  fetchOnMount?: boolean;
}

export const useSubscriptionStatus = (
  options?: UseSubscriptionStatusOptions
) => {
  const fetchOnMount = options?.fetchOnMount ?? true;
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(fetchOnMount);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      const status = await subscriptionAPI.getStatus();
      setSubscriptionStatus(status);
      setError(null);
      return status;
    } catch (err) {
      setError('Failed to load subscription status');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchOnMount) {
      return;
    }

    refresh().catch(() => {
      // Errors are surfaced via `error` state; no-op here.
    });
  }, [fetchOnMount, refresh]);

  return {
    subscriptionStatus,
    isLoading,
    error,
    refresh,
    setSubscriptionStatus,
  };
};

export type UseSubscriptionStatusReturn = ReturnType<typeof useSubscriptionStatus>;
